import React, { useMemo, useState } from "react";
import { FiActivity, FiRefreshCw, FiAlertTriangle, FiX } from "react-icons/fi";
import { useLogsData } from "../../hooks/useLogsData";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import type { LogEntry } from "../../API/Models";
import { ACTIVITY_META, ACTIVITY_TONES } from "../../Helper/activityMeta";
import { parseApiDate } from "../../Helper/dates";

/**
 * Live activity feed for the deployment page. Shows ALL recent system-log
 * events, and visually flags the AUTOMATIC ones : those that reach the site
 * without an operator (the ForexAPI sync, feed auto-publishes) : in green so
 * they stand out from manual actions. Backed by the same /api/logs the Logs
 * page uses, so it updates in real time.
 */

/** Compact French relative time. Parses the API timestamp as UTC (see parseApiDate). */
const relativeTime = (iso?: string): string => {
	const date = parseApiDate(iso);
	if (!date) return "";
	const then = date.getTime();
	const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
	if (s < 60) return "à l'instant";
	const m = Math.floor(s / 60);
	if (m < 60) return `il y a ${m} min`;
	const h = Math.floor(m / 60);
	if (h < 24) return `il y a ${h} h`;
	const d = Math.floor(h / 24);
	return d < 7 ? `il y a ${d} j` : new Date(then).toLocaleDateString("fr-FR");
};

/** Fallback label for a log type that isn't a known activity type. */
const humanizeType = (type: string): string => {
	if (!type) return "Activité";
	const s = type.replace(/[-_:]+/g, " ").trim();
	return s.charAt(0).toUpperCase() + s.slice(1);
};

const authorOf = (entry: LogEntry): string | null =>
	entry.user && entry.user.trim() && entry.user.trim().toUpperCase() !== "SYSTEM" ? entry.user.trim() : null;

export const ActivityRow: React.FC<{ entry: LogEntry; onSelect: (e: LogEntry) => void }> = ({ entry, onSelect }) => {
	const type = (entry.type ?? "").toLowerCase();
	const tags = (entry.tags ?? []).map((t) => (t ?? "").toLowerCase());
	const isAutomatic = tags.includes("automatic");
	const level = (entry.level ?? "").toLowerCase();
	const isError = level === "error";
	const isWarning = level === "warning";
	const isAlert = isError || isWarning;
	const meta = ACTIVITY_META[type];
	const label = meta?.label ?? humanizeType(type);
	// Errors are RED (rose), warnings amber; otherwise automatic events (exchange-rate
	// sync, auto-publish) are green; otherwise the known activity tone, else neutral slate.
	const toneKey = isError ? "rose" : isWarning ? "orange" : isAutomatic ? "emerald" : meta?.tone ?? "slate";
	const tone = ACTIVITY_TONES[toneKey] ?? ACTIVITY_TONES.slate;
	const highlightGreen = isAutomatic && !isAlert;
	const author = authorOf(entry);
	const hasDetails = !!(entry.details && entry.details.trim());
	const clickable = isAlert || hasDetails;
	const accent = isError
		? "border-l-2 border-rose-500 bg-rose-50/50"
		: isWarning
			? "border-l-2 border-orange-400 bg-orange-50/40"
			: highlightGreen
				? "border-l-2 border-emerald-400 bg-emerald-50/40"
				: "";
	return (
		<li
			className={`flex gap-3 px-3 py-2.5 ${accent} ${clickable ? "cursor-pointer hover:bg-slate-50" : ""}`}
			onClick={clickable ? () => onSelect(entry) : undefined}
			title={clickable ? "Voir le détail" : undefined}
		>
			<span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white ${tone.icon}`}>
				{isAlert ? <FiAlertTriangle /> : meta?.icon ?? <FiActivity />}
			</span>
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone.chip}`}>{label}</span>
					{isAutomatic && (
						<span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
							auto
						</span>
					)}
					<span className="truncate text-[11px] text-slate-400">{relativeTime(entry.updatedAt ?? entry.createdAt ?? undefined)}</span>
				</div>
				<p className="mt-1 truncate text-sm text-slate-700" title={entry.message ?? ""}>{entry.message}</p>
				<div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
					{author && <span>par {author}</span>}
					{isError && <span className="font-semibold text-rose-600">Voir l'erreur →</span>}
					{isWarning && <span className="font-semibold text-orange-600">Voir l'avertissement →</span>}
				</div>
			</div>
		</li>
	);
};

/** Modal showing the full message + details (and the error) for a clicked activity entry. */
export const ActivityDetailModal: React.FC<{ entry: LogEntry; onClose: () => void }> = ({ entry, onClose }) => {
	const level = (entry.level ?? "").toLowerCase();
	const isError = level === "error";
	const isWarning = level === "warning";
	const isAlert = isError || isWarning;
	const iconClass = isError ? "bg-rose-100 text-rose-600" : isWarning ? "bg-orange-100 text-orange-600" : "bg-slate-100 text-slate-500";
	const author = authorOf(entry) ?? "Système";
	const when = parseApiDate(entry.updatedAt ?? entry.createdAt);
	return (
		<div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm" onClick={onClose}>
			<div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
				<div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
					<span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconClass}`}>
						{isAlert ? <FiAlertTriangle /> : <FiActivity />}
					</span>
					<div className="min-w-0 flex-1">
						<h3 className="text-base font-bold text-slate-900">{entry.message || "Détail de l'activité"}</h3>
						<p className="mt-0.5 text-xs text-slate-500">
							{entry.type || "activité"} · par {author}
							{when ? ` · ${when.toLocaleString("fr-FR")}` : ""}
						</p>
					</div>
					<button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" title="Fermer">
						<FiX />
					</button>
				</div>
				<div className="max-h-[50vh] overflow-y-auto px-5 py-4">
					{entry.details && entry.details.trim() ? (
						<pre className="whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 text-xs text-slate-700">{entry.details}</pre>
					) : (
						<p className="text-sm text-slate-500">Aucun détail supplémentaire.</p>
					)}
					{entry.tags && entry.tags.length > 0 && (
						<div className="mt-3 flex flex-wrap gap-1.5">
							{entry.tags.filter(Boolean).map((t, i) => (
								<span key={`${t}-${i}`} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{t}</span>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const ActivityFeed: React.FC<{ target?: "preview" | "production"; refreshKey?: number }> = ({ target, refreshKey }) => {
	const { logs, loading, error, refresh, isRefreshing } = useLogsData({ limit: 80, includeDebug: false });
	const [selected, setSelected] = useState<LogEntry | null>(null);

	// Re-pull when the parent signals a deploy/promote just happened. `refresh`
	// gets a new identity each render, so we hold it in a ref and key the effect
	// only on refreshKey : otherwise it would re-fire every render.
	const refreshRef = React.useRef(refresh);
	refreshRef.current = refresh;
	React.useEffect(() => {
		if (typeof refreshKey === "number" && refreshKey > 0) refreshRef.current();
	}, [refreshKey]);

	// Scope to the selected environment: an entry tagged for the OTHER environment
	// is hidden; entries with no environment tag (e.g. the ForexAPI sync) are
	// environment-agnostic and always shown. Automatic events stay highlighted.
	const activity = useMemo(() => {
		const all = logs ?? [];
		if (!target) return all;
		return all.filter((l) => {
			const tags = (l.tags ?? []).map((t) => (t ?? "").toLowerCase());
			const hasPreview = tags.includes("preview");
			const hasProduction = tags.includes("production");
			if (!hasPreview && !hasProduction) return true; // env-agnostic → always
			return tags.includes(target);
		});
	}, [logs, target]);

	return (
		<aside className="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
			<header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
				<div className="flex items-center gap-2">
					<FiActivity className="text-slate-400" />
					<h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">Activité</h3>
					<span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
						<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> auto
					</span>
				</div>
				<button
					type="button"
					onClick={() => refresh()}
					className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
					title="Rafraîchir"
				>
					<FiRefreshCw className={loading || isRefreshing ? "animate-spin" : ""} />
				</button>
			</header>
			<div className="min-h-0 flex-1 overflow-y-auto">
				{error && <p className="px-4 py-3 text-sm text-rose-600">{error}</p>}
				{!error && loading && activity.length === 0 && (
					<div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-400">
						<LoadingSpinner size="sm" /> Chargement…
					</div>
				)}
				{!error && !loading && activity.length === 0 && (
					<p className="px-4 py-8 text-center text-sm text-slate-400">Aucune activité récente.</p>
				)}
				<ul className="divide-y divide-slate-100">
					{activity.map((entry, i) => (
						<ActivityRow key={entry.id ?? `${entry.type}-${i}`} entry={entry} onSelect={setSelected} />
					))}
				</ul>
			</div>
			{selected && <ActivityDetailModal entry={selected} onClose={() => setSelected(null)} />}
		</aside>
	);
};

export default ActivityFeed;
